import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Chip,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ExpandMore,
  School,
  MenuBook,
  AutoStories,
  Clear,
  FilterList,
} from '@mui/icons-material';
import { curriculumAPI } from '../services/api';

// 层级节点数据类型
interface HierarchyNode {
  id: number;
  name: string;
  type: 'subject' | 'version' | 'chapter';
  knowledgePointCount: number;
  schoolLevel?: string;
  orderIndex?: number;
  children?: HierarchyNode[];
}

// 选中状态类型
interface SelectedItems {
  subjects: number[];
  versions: number[];
  chapters: number[];
}

interface HierarchyFilterProps {
  onSelectionChange: (selection: SelectedItems) => void;
  disabled?: boolean;
}

const HierarchyFilter: React.FC<HierarchyFilterProps> = ({
  onSelectionChange,
  disabled = false,
}) => {
  const [hierarchyData, setHierarchyData] = useState<HierarchyNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedItems, setSelectedItems] = useState<SelectedItems>({
    subjects: [],
    versions: [],
    chapters: [],
  });
  const [expandedSubjects, setExpandedSubjects] = useState<Set<number>>(new Set());
  const [expandedVersions, setExpandedVersions] = useState<Set<number>>(new Set());

  // 加载层级数据
  const loadHierarchyData = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await curriculumAPI.getKnowledgePointHierarchyFilterData();
      
      // 转换后端数据为前端格式
      const formattedData: HierarchyNode[] = data.map((subject: any) => ({
        id: subject.id,
        name: subject.name,
        type: 'subject' as const,
        knowledgePointCount: subject.knowledgePointCount,
        children: subject.versions?.map((version: any) => ({
          id: version.id,
          name: version.name,
          type: 'version' as const,
          knowledgePointCount: version.knowledgePointCount,
          schoolLevel: version.schoolLevel,
          children: version.chapters?.map((chapter: any) => ({
            id: chapter.id,
            name: chapter.name,
            type: 'chapter' as const,
            knowledgePointCount: chapter.knowledgePointCount,
            orderIndex: chapter.orderIndex,
          })) || [],
        })) || [],
      }));

      setHierarchyData(formattedData);
    } catch (error: any) {
      console.error('加载层级筛选数据失败:', error);
      setError('加载层级数据失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHierarchyData();
  }, []);

  // 处理选择变化
  const handleSelectionChange = (
    type: keyof SelectedItems,
    id: number,
    checked: boolean
  ) => {
    const newSelection = { ...selectedItems };
    
    if (checked) {
      if (!newSelection[type].includes(id)) {
        newSelection[type] = [...newSelection[type], id];
      }
    } else {
      newSelection[type] = newSelection[type].filter(itemId => itemId !== id);
    }

    setSelectedItems(newSelection);
    onSelectionChange(newSelection);
  };

  // 处理学科展开/折叠
  const handleSubjectToggle = (subjectId: number) => {
    const newExpanded = new Set(expandedSubjects);
    if (newExpanded.has(subjectId)) {
      newExpanded.delete(subjectId);
    } else {
      newExpanded.add(subjectId);
    }
    setExpandedSubjects(newExpanded);
  };

  // 处理版本展开/折叠
  const handleVersionToggle = (versionId: number) => {
    const newExpanded = new Set(expandedVersions);
    if (newExpanded.has(versionId)) {
      newExpanded.delete(versionId);
    } else {
      newExpanded.add(versionId);
    }
    setExpandedVersions(newExpanded);
  };

  // 清除所有选择
  const handleClearAll = () => {
    const emptySelection = {
      subjects: [],
      versions: [],
      chapters: [],
    };
    setSelectedItems(emptySelection);
    onSelectionChange(emptySelection);
  };

  // 获取选中项的总数
  const getTotalSelectedCount = () => {
    return selectedItems.subjects.length + 
           selectedItems.versions.length + 
           selectedItems.chapters.length;
  };

  // 渲染章节列表
  const renderChapters = (chapters: HierarchyNode[]) => {
    return chapters.map((chapter) => (
      <ListItem key={chapter.id} dense disablePadding>
        <ListItemButton
          onClick={() => handleSelectionChange('chapters', chapter.id, !selectedItems.chapters.includes(chapter.id))}
          disabled={disabled}
          sx={{ pl: 6 }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedItems.chapters.includes(chapter.id)}
                size="small"
                disabled={disabled}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoStories sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2">
                  {chapter.name}
                </Typography>
                <Chip 
                  label={chapter.knowledgePointCount} 
                  size="small" 
                  variant="outlined"
                  color="primary"
                />
              </Box>
            }
            sx={{ margin: 0, width: '100%' }}
          />
        </ListItemButton>
      </ListItem>
    ));
  };

  // 渲染版本列表
  const renderVersions = (versions: HierarchyNode[]) => {
    return versions.map((version) => (
      <Box key={version.id}>
        <ListItem dense disablePadding>
          <ListItemButton
            onClick={() => handleVersionToggle(version.id)}
            disabled={disabled}
            sx={{ pl: 3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedItems.versions.includes(version.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleSelectionChange('versions', version.id, e.target.checked);
                    }}
                    size="small"
                    disabled={disabled}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MenuBook sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {version.schoolLevel ? `${version.schoolLevel} - ${version.name}` : version.name}
                    </Typography>
                    <Chip 
                      label={version.knowledgePointCount} 
                      size="small" 
                      variant="outlined"
                      color="secondary"
                    />
                  </Box>
                }
                sx={{ margin: 0, flex: 1 }}
              />
              <ExpandMore
                sx={{
                  transform: expandedVersions.has(version.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </Box>
          </ListItemButton>
        </ListItem>
        <Collapse in={expandedVersions.has(version.id)}>
          <List dense>
            {renderChapters(version.children || [])}
          </List>
        </Collapse>
      </Box>
    ));
  };

  // 渲染学科列表
  const renderSubjects = () => {
    return hierarchyData.map((subject) => (
      <Accordion 
        key={subject.id}
        expanded={expandedSubjects.has(subject.id)}
        onChange={() => handleSubjectToggle(subject.id)}
        disabled={disabled}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{ 
            '& .MuiAccordionSummary-content': { 
              alignItems: 'center', 
              gap: 1 
            } 
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedItems.subjects.includes(subject.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  handleSelectionChange('subjects', subject.id, e.target.checked);
                }}
                size="small"
                disabled={disabled}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <School sx={{ fontSize: 18, color: 'primary.main' }} />
                <Typography variant="subtitle2" fontWeight="medium">
                  {subject.name}
                </Typography>
                <Chip 
                  label={subject.knowledgePointCount} 
                  size="small" 
                  color="primary"
                />
              </Box>
            }
            sx={{ margin: 0 }}
          />
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <List dense>
            {renderVersions(subject.children || [])}
          </List>
        </AccordionDetails>
      </Accordion>
    ));
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography color="text.secondary">加载层级数据中...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {/* 标题和操作栏 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList color="primary" />
            <Typography variant="h6" fontWeight="medium">
              层级筛选
            </Typography>
            {getTotalSelectedCount() > 0 && (
              <Chip 
                label={`已选 ${getTotalSelectedCount()} 项`} 
                size="small" 
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          {getTotalSelectedCount() > 0 && (
            <IconButton 
              size="small" 
              onClick={handleClearAll}
              disabled={disabled}
              title="清除所有选择"
            >
              <Clear />
            </IconButton>
          )}
        </Box>

        {/* 层级树 */}
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {hierarchyData.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              暂无层级数据
            </Typography>
          ) : (
            renderSubjects()
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default HierarchyFilter;